import React, { useState, useEffect, useCallback, ChangeEvent, FormEvent } from "react";
import {
  getAppointments,
  getNewAppointments,
  updateAppointmentStatus,
  rescheduleAppointment,
  cancelAppointmentArtist,
  updateAppointmentDetails,
  acceptRescheduleRequest,
  declineRescheduleRequest,
  exportAppointmentsIcs,
} from "./api";
import { Location } from "./types";
import {
  PenIcon, CalendarIcon, LocationIcon, ClockIcon, UserIcon, MailIcon, NoteIcon,
} from "./components/Icons";
import detailMessage from "./detailMessage";
import { useLang } from "./LangContext";
import "./BookingsOverview.css";

const PAGE_SIZE = 5;
const ALL_MONTHS = "all";

interface AppointmentLocation {
  id: number;
  address: string;
}

interface Appointment {
  id: number;
  start_time: string;
  duration: number;
  status: string;
  customer_name: string;
  customer_e_mail: string;
  description?: string | null;
  location?: AppointmentLocation | null;
  location_address?: string | null;
  reschedule_day?: string | null;
  reschedule_start_time?: string | null;
}

interface EditForm {
  day: string;
  time: string;
  duration: number | string;
  description: string;
  location_id: string;
}

interface Props {
  token: string;
  refreshTick?: number;
  locations?: Location[];
}

export default function BookingsOverview({ token, refreshTick = 0, locations = [] }: Props) {
  const { t, lang } = useLang();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [total, setTotal] = useState(0);
  const [requestedAppointments, setRequestedAppointments] = useState(0);
  const [showDeclined, setShowDeclined] = useState(false);
  // ALL_MONTHS = no filter (default), 0..11 = specific month in the current year.
  const [selectedMonth, setSelectedMonth] = useState<number | typeof ALL_MONTHS>(ALL_MONTHS);
  const [year] = useState(new Date().getFullYear());
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({
    day: "",
    time: "",
    duration: 1,
    description: "",
    location_id: "",
  });
  const [actionError, setActionError] = useState("");
  const [actionBusy, setActionBusy] = useState(false);

  const fetchAppointments = useCallback(async (pageOverride: number) => {
    try {
      const data = await getAppointments(token, {
        showDeclined,
        page: pageOverride,
        pageSize: PAGE_SIZE,
        month: selectedMonth === ALL_MONTHS ? null : selectedMonth,
        year: selectedMonth === ALL_MONTHS ? null : year,
      });
      setAppointments(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Error fetching appointments:", err);
      setAppointments([]);
      setTotal(0);
    }
    try {
      setRequestedAppointments(await getNewAppointments(token));
    } catch (err) {
      console.error("Error fetching new-appointments count:", err);
    }
  }, [token, showDeclined, selectedMonth, year]);

  // Reset to page 1 whenever filters change.
  useEffect(() => {
    setPage(1);
  }, [showDeclined, selectedMonth]);

  useEffect(() => {
    fetchAppointments(page);
  }, [fetchAppointments, page, refreshTick]);

  const handleStatusUpdate = async (appointmentId: number, status: string) => {
    try {
      await updateAppointmentStatus(appointmentId, status, token);
      fetchAppointments(page);
    } catch (err) {
      console.error("Error updating appointment status:", err);
    }
  };

  const startEdit = (appointment: Appointment) => {
    const start = new Date(appointment.start_time);
    const pad = (n: number) => String(n).padStart(2, "0");
    const day = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
    const time = `${pad(start.getHours())}:${pad(start.getMinutes())}`;
    setEditingId(appointment.id);
    setEditForm({
      day,
      time,
      duration: appointment.duration,
      description: appointment.description || "",
      location_id: appointment.location ? String(appointment.location.id) : "",
    });
    setActionError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setActionError("");
  };

  const submitEdit = async (e: FormEvent) => {
    e.preventDefault();
    setActionError("");
    setActionBusy(true);
    try {
      const original = appointments.find((a) => a.id === editingId);
      const originalStart = original ? new Date(original.start_time) : null;
      const pad = (n: number) => String(n).padStart(2, "0");
      const originalDay = originalStart
        ? `${originalStart.getFullYear()}-${pad(originalStart.getMonth() + 1)}-${pad(originalStart.getDate())}`
        : null;
      const originalTime = originalStart
        ? `${pad(originalStart.getHours())}:${pad(originalStart.getMinutes())}`
        : null;
      const timingChanged =
        !original ||
        editForm.day !== originalDay ||
        editForm.time !== originalTime ||
        parseFloat(String(editForm.duration)) !== original.duration;

      if (timingChanged) {
        const startIso = `${editForm.day}T${editForm.time}:00`;
        await rescheduleAppointment(
          editingId!,
          {
            day: editForm.day,
            start_time: startIso,
            duration: parseFloat(String(editForm.duration)),
          },
          token,
        );
      }
      const detailsPayload: { description: string; clear_location?: boolean; location_id?: number } = {
        description: editForm.description || "",
      };
      if (editForm.location_id === "") {
        detailsPayload.clear_location = true;
      } else {
        detailsPayload.location_id = parseInt(editForm.location_id, 10);
      }
      await updateAppointmentDetails(editingId!, detailsPayload, token);
      setEditingId(null);
      fetchAppointments(page);
    } catch (err) {
      setActionError(detailMessage(err, "Failed to update appointment"));
    } finally {
      setActionBusy(false);
    }
  };

  const cancelAppointment = async (appointmentId: number) => {
    if (!window.confirm(t.cancelAppointmentConfirm)) {
      return;
    }
    setActionBusy(true);
    setActionError("");
    try {
      await cancelAppointmentArtist(appointmentId, token);
      fetchAppointments(page);
    } catch (err) {
      setActionError(detailMessage(err, "Failed to cancel appointment"));
    } finally {
      setActionBusy(false);
    }
  };

  const handleAcceptReschedule = async (appointmentId: number) => {
    setActionBusy(true);
    setActionError("");
    try {
      await acceptRescheduleRequest(appointmentId, token);
      fetchAppointments(page);
    } catch (err) {
      setActionError(detailMessage(err, "Failed to accept reschedule"));
    } finally {
      setActionBusy(false);
    }
  };

  const handleDeclineReschedule = async (appointmentId: number) => {
    setActionBusy(true);
    setActionError("");
    try {
      await declineRescheduleRequest(appointmentId, token);
      fetchAppointments(page);
    } catch (err) {
      setActionError(detailMessage(err, "Failed to decline reschedule"));
    } finally {
      setActionBusy(false);
    }
  };

  const monthNames = Array.from({ length: 12 }, (_, i) =>
    new Intl.DateTimeFormat(lang, { month: "long" }).format(new Date(2022, i))
  );

  const isPastAppointment = (endTime: Date) => {
    const now = new Date();
    return endTime < now;
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="bookings-overview">
      <div className="bookings-header">
        <h3>
          {t.appointmentsHeading}
          {requestedAppointments > 0 && (
            <span className="notification-badge">{requestedAppointments}</span>
          )}
        </h3>
        <button className="button button-sm button-outline" onClick={() => exportAppointmentsIcs(token)}>
          {t.exportIcsButton}
        </button>
      </div>

      <div className="bookings-filters">
        <div className="filter-checkbox">
          <input
            type="checkbox"
            id="showDeclined"
            checked={showDeclined}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setShowDeclined(e.target.checked)}
          />
          <label htmlFor="showDeclined">{t.includeDeclined}</label>
        </div>

        <div className="month-dropdown">
          <label htmlFor="monthSelect">{t.filterByMonth}</label>
          <select
            id="monthSelect"
            value={selectedMonth}
            onChange={(e) =>
              setSelectedMonth(
                e.target.value === ALL_MONTHS ? ALL_MONTHS : parseInt(e.target.value, 10)
              )
            }
          >
            <option value={ALL_MONTHS}>{t.allMonths}</option>
            {monthNames.map((name, index) => (
              <option key={name} value={index}>
                {name} {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {appointments.length === 0 ? (
        <div className="no-appointments">
          {selectedMonth === ALL_MONTHS
            ? t.noAppointmentsYet
            : t.noAppointmentsMonth}
        </div>
      ) : (
        <ul className="appointments-list">
          {appointments.map((appointment) => {
            const endTime = new Date(appointment.start_time);
            endTime.setHours(endTime.getHours() + appointment.duration);

            return (
              <li
                key={appointment.id}
                className={`appointment-item ${isPastAppointment(endTime) ? "past-appointment" : ""}`}
              >
                <div className="booking-appointment-details">
                  <div className="appointment-grid">
                    <div className="appointment-grid-cell">
                      <div className="appointment-grid-label">
                        <CalendarIcon /> <span>{t.colDate}</span>
                      </div>
                      <div className="appointment-grid-value">
                        {new Date(appointment.start_time).toLocaleDateString(lang, {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </div>
                    </div>
                    <div className="appointment-grid-cell">
                      <div className="appointment-grid-label">
                        <UserIcon /> <span>{t.colCustomer}</span>
                      </div>
                      <div className="appointment-grid-value">{appointment.customer_name}</div>
                    </div>
                    <div className="appointment-grid-cell">
                      <div className="appointment-grid-label">
                        <LocationIcon /> <span>{t.colLocation}</span>
                      </div>
                      <div className="appointment-grid-value">
                        {appointment.location
                          ? appointment.location.address
                          : appointment.location_address || <span className="muted">—</span>}
                      </div>
                    </div>
                    <div className="appointment-grid-cell">
                      <div className="appointment-grid-label">
                        <MailIcon /> <span>{t.colEmail}</span>
                      </div>
                      <div className="appointment-grid-value truncate" title={appointment.customer_e_mail}>
                        {appointment.customer_e_mail}
                      </div>
                    </div>
                    <div className="appointment-grid-cell">
                      <div className="appointment-grid-label">
                        <ClockIcon /> <span>{t.colTime}</span>
                      </div>
                      <div className="appointment-grid-value">
                        {new Date(appointment.start_time).toLocaleTimeString(lang, {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })}{" "}
                        ({appointment.duration}h)
                      </div>
                    </div>
                    <div className="appointment-grid-cell">
                      <div className="appointment-grid-label">
                        <NoteIcon /> <span>{t.colDescription}</span>
                      </div>
                      <div className="appointment-grid-value">
                        {appointment.description || <span className="muted">—</span>}
                      </div>
                    </div>
                  </div>
                  <div className="appointment-status-row">
                    <span className={`appointment-status ${appointment.status.toLowerCase()}`}>
                      {appointment.status === "RESCHEDULE_REQUESTED" ? t.rescheduleRequestLabel : appointment.status}
                    </span>
                  </div>
                  {appointment.status === "RESCHEDULE_REQUESTED" && appointment.reschedule_start_time && (
                    <div className="reschedule-request-info">
                      <div className="reschedule-time-row">
                        <span className="reschedule-time-label">{t.rescheduleCurrentTime}:</span>
                        <span>{new Date(appointment.start_time).toLocaleString(lang === 'de' ? 'de-DE' : 'en-US', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <div className="reschedule-time-row">
                        <span className="reschedule-time-label">{t.rescheduleProposedTime}:</span>
                        <span>{new Date(appointment.reschedule_start_time).toLocaleString(lang === 'de' ? 'de-DE' : 'en-US', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                  )}
                </div>
                {editingId === appointment.id ? (
                  <form className="appointment-edit-form" onSubmit={submitEdit}>
                    <label>
                      {t.colDate}
                      <input
                        type="date"
                        value={editForm.day}
                        onChange={(e) => setEditForm({ ...editForm, day: e.target.value })}
                        required
                      />
                    </label>
                    <label>
                      {t.colTime}
                      <input
                        type="time"
                        value={editForm.time}
                        onChange={(e) => setEditForm({ ...editForm, time: e.target.value })}
                        required
                      />
                    </label>
                    <label>
                      {t.duration} (h)
                      <input
                        type="number"
                        step="0.5"
                        min="0.5"
                        max="8"
                        value={editForm.duration}
                        onChange={(e) => setEditForm({ ...editForm, duration: e.target.value })}
                        required
                      />
                    </label>
                    <label>
                      {t.colLocation}
                      <select
                        value={editForm.location_id}
                        onChange={(e) => setEditForm({ ...editForm, location_id: e.target.value })}
                      >
                        <option value="">{t.noLocation}</option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.name}{loc.is_default ? ` (${t.defaultBadge.toLowerCase()})` : ""}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      {t.colDescription}
                      <textarea
                        rows={3}
                        maxLength={500}
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      />
                    </label>
                    {actionError && (
                      <div className="error-message">{actionError}</div>
                    )}
                    <div className="appointment-actions">
                      <button type="submit" className="button accept" disabled={actionBusy}>
                        {actionBusy ? t.savingButton : t.saveButton}
                      </button>
                      <button
                        type="button"
                        className="button decline"
                        onClick={cancelEdit}
                        disabled={actionBusy}
                      >
                        {t.discardButton}
                      </button>
                    </div>
                  </form>
                ) : (
                  ["REQUESTED", "ACCEPTED", "PENDING_CONFIRMATION", "RESCHEDULE_REQUESTED"].includes(appointment.status) && (
                    <div className="appointment-actions">
                      {appointment.status === "RESCHEDULE_REQUESTED" && (
                        <>
                          <button
                            className="button accept"
                            onClick={() => handleAcceptReschedule(appointment.id)}
                            disabled={actionBusy}
                          >
                            {t.acceptReschedule}
                          </button>
                          <button
                            className="button decline"
                            onClick={() => handleDeclineReschedule(appointment.id)}
                            disabled={actionBusy}
                          >
                            {t.declineReschedule}
                          </button>
                        </>
                      )}
                      {appointment.status === "REQUESTED" && (
                        <>
                          <button
                            className="button accept"
                            onClick={() => handleStatusUpdate(appointment.id, "ACCEPTED")}
                          >
                            {t.acceptButton}
                          </button>
                          <button
                            className="button decline"
                            onClick={() => handleStatusUpdate(appointment.id, "DECLINED")}
                          >
                            {t.declineButton}
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        className="button button-edit"
                        onClick={() => startEdit(appointment)}
                        disabled={actionBusy}
                      >
                        <PenIcon /> {t.editButton}
                      </button>
                      {appointment.status === "ACCEPTED" && (
                        <button
                          type="button"
                          className="button logout"
                          onClick={() => cancelAppointment(appointment.id)}
                          disabled={actionBusy}
                        >
                          {t.cancelButton}
                        </button>
                      )}
                    </div>
                  )
                )}
              </li>
            );
          })}
        </ul>
      )}

      {total > PAGE_SIZE && (
        <div className="bookings-pagination">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            {t.paginationPrev}
          </button>
          <span className="bookings-pagination-status">
            {t.paginationStatus(page, totalPages, total)}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            {t.paginationNext}
          </button>
        </div>
      )}
    </div>
  );
}
